import React from "react";
import { useVisibility, type VisibilityLevel } from "@/contexts/VisibilityContext";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

interface CompactVisibilityRowProps {
  moduleType: "page" | "section";
  moduleId: string;
  title: string;
}

export function CompactVisibilityRow({
  moduleType,
  moduleId,
  title,
}: CompactVisibilityRowProps) {
  const { getVisibility, setVisibility, isPublicMode } = useVisibility();
  const current = getVisibility(moduleId);

  const accessLevel: Exclude<VisibilityLevel, "hidden"> = current === "hidden" ? "public" : current;
  const hidden = current === "hidden";

  const onChangeAccess = async (value: VisibilityLevel) => {
    try {
      await setVisibility({ moduleId, moduleType, visibility: value });
    } catch (error) {
      console.error("Failed to update visibility:", error);
    }
  };

  const onToggleHidden = async (nextHidden: boolean) => {
    if (nextHidden) {
      await setVisibility({ moduleId, moduleType, visibility: "hidden" });
    } else {
      await setVisibility({ moduleId, moduleType, visibility: accessLevel });
    }
  };

  const levels: { key: Exclude<VisibilityLevel, "hidden">; label: string }[] = [
    { key: "public", label: "P" },
    { key: "member", label: "M" },
    { key: "admin", label: "A" },
  ];

  return (
    <div
      className={cn(
        "flex items-center justify-between py-2 px-3 border-b border-border/50 hover:bg-muted/30 transition-colors",
        isPublicMode && "opacity-50 pointer-events-none"
      )}
    >
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{title}</span>
        <span className="text-xs text-muted-foreground truncate block">{moduleId}</span>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {/* Visibility level pills */}
        <div className="flex gap-1">
          {levels.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => onChangeAccess(key)}
              disabled={isPublicMode}
              className={cn(
                "w-7 h-7 rounded text-xs font-bold transition-colors",
                accessLevel === key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:bg-muted"
              )}
              title={key.charAt(0).toUpperCase() + key.slice(1)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Hidden toggle */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Hide</span>
          <Switch
            checked={hidden}
            onCheckedChange={onToggleHidden}
            disabled={isPublicMode}
            className="scale-75"
          />
        </div>
      </div>
    </div>
  );
}
